import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import BrandLogo from "./BrandLogo.js";
import {
  BEHAVIORAL_PATTERNS_ROUTE,
  BEHAVIORAL_PATTERNS_TITLE,
  QUICK_CLARITY_ROUTE,
  QUICK_CLARITY_TITLE,
  DEEP_REFLECTION_ROUTE,
  DEEP_REFLECTION_TITLE,
  ACTIONS_ROUTE,
  ACTIONS_TITLE
} from "../constants/routes.js";

const Header = ({ isVerified, isSubscriber, onLogout, verifiedEmail }) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const drawerRef = useRef(null);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isOpen) return;
    const drawer = drawerRef.current;
    const focusableSelector = 'a, button, [tabindex]:not([tabindex="-1"])';
    const focusables = drawer ? Array.from(drawer.querySelectorAll(focusableSelector)) : [];
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const previousFocus = document.activeElement;

    if (first) {
      first.focus();
    }

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        return;
      }
      if (event.key !== "Tab" || focusables.length === 0) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (previousFocus && previousFocus.focus) {
        previousFocus.focus();
      }
    };
  }, [isOpen]);

  const isReflectionPage =
    location.pathname === "/" ||
    location.pathname === "/personalized" ||
    location.pathname === DEEP_REFLECTION_ROUTE;
  const showBrand = location.pathname !== "/login";
  const isActive = (path) => location.pathname === path;
  const isBehavioralPatternsActive =
    location.pathname === BEHAVIORAL_PATTERNS_ROUTE || location.pathname.startsWith(`${BEHAVIORAL_PATTERNS_ROUTE}/`);
  const isDeepReflectionActive = location.pathname === DEEP_REFLECTION_ROUTE;
  const isQuickClarityActive = location.pathname === QUICK_CLARITY_ROUTE;
  const isActionsActive = location.pathname === ACTIONS_ROUTE;

  const isLoginPage = location.pathname === "/login";

  return (
    <header
      className={[
        "app-header",
        isReflectionPage ? "app-header--reflection" : "",
        isLoginPage ? "app-header--login" : "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {showBrand ? (
        <div className="brand-header app-header__brand">
          <BrandLogo />
        </div>
      ) : null}
      <button
        className={`menu-button ${isOpen ? "menu-button--open" : ""}`}
        type="button"
        aria-label={isOpen ? "Close menu" : "Open menu"}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <span className="menu-button__bar" />
        <span className="menu-button__bar" />
        <span className="menu-button__bar" />
      </button>
      {isOpen ? (
        <>
          <button
            className="menu-backdrop"
            type="button"
            aria-label="Close menu"
            onClick={() => setIsOpen(false)}
          />
          <nav
            className="menu-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Main menu"
            ref={drawerRef}
          >
            <div className="menu-section">
              <p className="menu-section__title">MAIN</p>
              <Link className={`menu-link ${isActive("/") ? "menu-link--active" : ""}`} to="/">
                Home
              </Link>
              {isVerified ? (
                <Link
                  className={`menu-link ${isBehavioralPatternsActive ? "menu-link--active" : ""}`}
                  to={BEHAVIORAL_PATTERNS_ROUTE}
                >
                  {BEHAVIORAL_PATTERNS_TITLE}
                </Link>
              ) : null}
              {isVerified ? (
                <Link className={`menu-link ${isActive("/history") ? "menu-link--active" : ""}`} to="/history">
                  Past Reflections
                </Link>
              ) : null}
            </div>

            <div className="menu-section">
              <p className="menu-section__title">TOOLS</p>
              {isVerified ? (
                <>
                  <Link
                    className={`menu-link ${isQuickClarityActive ? "menu-link--active" : ""}`}
                    to={QUICK_CLARITY_ROUTE}
                  >
                    {QUICK_CLARITY_TITLE}
                  </Link>
                  <Link
                    className={`menu-link ${isDeepReflectionActive ? "menu-link--active" : ""}`}
                    to={DEEP_REFLECTION_ROUTE}
                  >
                    {DEEP_REFLECTION_TITLE}
                  </Link>
                  <Link className={`menu-link ${isActionsActive ? "menu-link--active" : ""}`} to={ACTIONS_ROUTE}>
                    {ACTIONS_TITLE}
                  </Link>
                </>
              ) : null}
              {isVerified ? (
                <Link className={`menu-link ${isActive("/billing") ? "menu-link--active" : ""}`} to="/billing">
                  Billing
                </Link>
              ) : null}
              <Link className={`menu-link ${isActive("/pricing") ? "menu-link--active" : ""}`} to="/pricing">
                Pricing
              </Link>
              <Link className={`menu-link ${isActive("/guide") ? "menu-link--active" : ""}`} to="/guide">
                Guide
              </Link>
            </div>

            <div className="menu-section">
              <p className="menu-section__title">ACCOUNT</p>
              {isVerified ? (
                <>
                  <Link className={`menu-link ${isActive("/profile") ? "menu-link--active" : ""}`} to="/profile">
                    Profile
                  </Link>
                  <button
                    type="button"
                    className="menu-link menu-link--button"
                    onClick={() => {
                      onLogout();
                      navigate("/login");
                    }}
                  >
                    Logout
                  </button>
                </>
              ) : (
                <Link className={`menu-link ${isActive("/login") ? "menu-link--active" : ""}`} to="/login">
                  Login
                </Link>
              )}
            </div>
          </nav>
        </>
      ) : null}
    </header>
  );
};

export default Header;
